/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
} from 'react-native';
import { isWeb, fullWidth, isAndroid, isMobileWeb } from '@global/screens';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseDate, currency, exportCsvApi } from '@global/helpers';
import Colors from '@global/colors';
import * as Layout from '@components/screen/Layout';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import Button from '@components/form/Button';
import ModalFull from '@components/modal/ModalFull';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { filterAdded, paramsReseted } from '@/store/params';
import { addDays, endOfDay, formatISO, startOfDay } from 'date-fns';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetReturnOrderListQuery,
  useGetReturnOrderCountQuery,
  useGetReturnOrderInfiniteListInfiniteQuery,
} from '@api/listApi';

const dimension = Dimensions.get('screen');

const tableStructure = {
  customer: {
    head: 'Customer Name',
    body: 'customer.name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  source_document: {
    head: 'Source Document',
    body: 'source_order.reference_code',
  },
  reference_code: {
    head: 'Return Number',
    body: 'reference_code',
  },
  do_in_number: {
    head: 'DO-IN Number',
    body: 'delivery_order_number',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'sales_team.name',
    hide: true,
  },
  scheduled_date: {
    head: 'Schedule Date',
    type: 'date',
    body: 'scheduled_date',
  },
  total_amount: {
    head: 'Total Value',
    type: 'currency',
    symbol: true,
    body: 'subtotal',
  },
  status: {
    head: 'Status',
    type: 'status',
    body: 'status',
  },
  // Temporary command,not used now
  // invoice_status: {
  //   head: 'R-Invoice Status',
  //   type: 'status',
  //   body: 'invoice_status',
  //   hide: true,
  // },
};

// UI filter shape → API query filter (was the inline transform in the old finalParam):
//  - `keyword` is unwrapped from the search box's { $regex } object
//  - date range (scheduled_date), status and channel pass through as-is
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const kwSource = rawFilter.keyword ?? rawFilter.reference_code;
  const kw = kwSource?.$regex ?? kwSource;
  if (kw) {
    filter.keyword = kw;
  }
  if (rawFilter.scheduled_date) {
    filter.scheduled_date = rawFilter.scheduled_date;
  }
  if (rawFilter.status) {
    filter.status = rawFilter.status;
  }
  if (rawFilter.channel) {
    filter.channel = rawFilter.channel;
  }
  return filter;
};

const statuses = [
  { name: 'Draft', value: 'draft' },
  { name: 'Pending Approval', value: 'need_approval' },
  { name: 'Approved', value: 'approved' },
  { name: 'Cancelled', value: 'cancelled' },
];

const return_invoice_statuses = [
  { name: 'No Invoice Yet', value: 'nothing_to_invoice' },
  { name: 'Partially Invoiced', value: 'to_invoice' },
  { name: 'Fully Invoiced', value: 'fully_invoiced' },
  { name: 'Fully Paid', value: 'fully_paid' },
];

const channels = [
  { name: 'GT', value: 'GT' },
  { name: 'MT', value: 'MT' },
];

const channelsExport = [
  { name: 'All', value: 'all' },
  { name: 'GT', value: 'gt' },
  { name: 'MT', value: 'mt' },
];

// Restrict the sales-team picker to the user's assigned teams (empty = all).
const customSalesTeamFilter = (userData) => {
  if (userData?.sales_team?.length) {
    return {
      _id: { $in: userData.sales_team.map((team) => team.id) },
    };
  } else {
    return {};
  }
};

const getTitleScreen = (referenceCode) => {
  return referenceCode ? referenceCode : '-';
};

const getCustomerName = (customer) => {
  return customer && customer.name ? customer.name : '-';
};

const ReturnOrderScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const userData = useSelector((state) => state.auth.user);
  const showReturnOrdersExport = useSelector(
    (state) => !!state.modal.showReturnOrdersExport,
  );
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    from_date: '',
    to_date: '',
    sales_team: '',
    channel: '',
    status: '',
    return_invoice_status: '',
  });
  const [draftFilter, setDraftFilter] = useState({});

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetReturnOrderListQuery,
    countHook: useGetReturnOrderCountQuery,
    infiniteHook: useGetReturnOrderInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  // Current search text, derived from the applied keyword filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── effects ──
  // Staged filter draft (date / status / channel), seeded on open.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const draftScheduledHandler = (value, type) => {
    setDraftFilter((f) => ({
      ...f,
      scheduled_date: {
        ...f.scheduled_date,
        [type]: new Date(value).toISOString(),
      },
    }));
  };

  const goToDetail = (data) => {
    openChild('ReturnOrderDetailScreen', {
      titleScreen: getTitleScreen(data.reference_code),
      name: getCustomerName(data.customer),
      id: data._id,
    });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }

      if (!filterExport.sales_team?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter sales team must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }

      const payload = {
        scheduled_date: {
          $gte: filterExport.from_date,
          $lte: filterExport.to_date,
        },
      };

      if (filterExport.sales_team?.[0]?.id != 'all') {
        payload['sales_team.id'] = {
          $in: filterExport.sales_team.map((st) => st.id),
        };
      }

      if (filterExport.channel != 'all') {
        payload['customer.market_type.code'] = {
          $in: [
            filterExport.channel.toUpperCase(),
            filterExport.channel.toLowerCase(),
          ],
        };
      }

      if (filterExport.return_invoice_status) {
        payload['return_invoice_status'] =
          filterExport['return_invoice_status'];
      }

      if (filterExport.status) {
        payload['status'] = filterExport['status'];
      }

      await exportCsvApi('return-orders', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });

      setFilterExport({
        from_date: '',
        to_date: '',
        sales_team: '',
        channel: '',
        status: '',
        return_invoice_status: '',
      });

      setExporting(false);
    } catch (error) {
      setExporting(false);
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description: error?.message || 'Something went wrong',
          isConfirm: false,
        }),
      );
    }
  };

  const renderTitleWeb = () =>
    isWeb ? (
      <Layout.Title title="Return Order">
        <Button
          type="primary"
          rounded={true}
          buttonStyle={{ width: 90, marginRight: 10 }}
          title={
            <SociollaText
              type="medium"
              style={{ fontSize: 14, color: Colors.white }}>
              Export
            </SociollaText>
          }
          onPress={() => {
            dispatch(
              showPopup({
                name: 'showReturnOrdersExport',
                value: true,
              }),
            );
          }}
        />
      </Layout.Title>
    ) : (
      false
    );

  const _getReturnOrderTablePropsWeb = () => ({
    filterName: scope,
    name: 'return-order',
    structures: tableStructure,
    loading: list.isLoading,
    refetching: list.isFetching,
    data: rows,
    searchKey: 'keyword',
    searchPlaceholder: 'Search by return number, customer name',
    searchValue: searchValue,
    searchWidth: 360,
    hasCallback: true,
    totalData: count.data,
    skip: params.skip,
    limit: params.limit,
    onClickRow: ({ data }) => goToDetail(data),
  });

  const renderCustomerName = (data) => (
    <View style={[styles.orderItemSection, { alignItems: 'flex-start' }]}>
      <SociollaText type="medium" style={styles.customerNameText}>
        {data.customer && data.customer.name ? data.customer.name : '-'}
      </SociollaText>
      <StatusBadge status={data.status} />
    </View>
  );

  const renderReferenceCode = (data) => (
    <SociollaText style={styles.containerReferenceCode}>
      {data.reference_code ? data.reference_code : '-'}
    </SociollaText>
  );

  const renderScheduledDate = (data) => (
    <View style={styles.containerScheduleData}>
      <Image
        style={styles.imageScheduleData}
        width={19}
        height={14}
        svg={true}
        source={require('@assets/img/icons/menu/calendar.svg')}
      />
      <SociollaText style={styles.scheduleText}>
        {data.scheduled_date ? parseDate(data.scheduled_date) : '-'}
      </SociollaText>
    </View>
  );

  const renderTotalAmount = (data) => (
    <SociollaText type="medium" style={styles.totalAmountText}>
      {currency(data.total_amount, true)}
    </SociollaText>
  );

  const renderOrderItem = (data) => (
    <View style={styles.orderItemSection}>
      {renderScheduledDate(data)}
      {renderTotalAmount(data)}
    </View>
  );

  const _renderItemTableMobile = () => ({
    renderItem: ({ data, index }) => (
      <TouchableOpacity
        activeOpacity={0.6}
        style={styles.orderItem}
        onPress={() => goToDetail(data)}
        key={index}>
        {renderCustomerName(data)}
        {renderReferenceCode(data)}
        {renderOrderItem(data)}
      </TouchableOpacity>
    ),
  });

  const _getReturnOrderTablePropsMobile = () => ({
    filterName: scope,
    searchKey: 'keyword',
    placeholder: 'Search by return number, customer name',
    searchValue: searchValue,
    loading: list.isLoading,
    refetching: list.isFetching,
    loadingLoadMore: list.isFetchingNextPage,
    hasNextPage: list.hasNextPage,
    onLoadMore: list.fetchNextPage,
    onRefresh: list.refetch,
    data: rows,
    totalData: count.data,
    ..._renderItemTableMobile(),
  });

  const renderWebTable = () => <Table {..._getReturnOrderTablePropsWeb()} />;

  const renderMobileTable = () => (
    <TableMobile {..._getReturnOrderTablePropsMobile()} />
  );

  const renderTableContent = () => {
    return isWeb ? renderWebTable() : renderMobileTable();
  };

  const renderDateLabel = () => (
    <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>Date</SociollaText>
  );

  const _getStartDatePickerProps = () => ({
    label: 'Start Date',
    name: 'scheduled_date',
    style: styles.startDatePicker,
    disableAfterDate: draftFilter.scheduled_date?.$lte,
    value: draftFilter.scheduled_date?.$gte,
    onChange: (value) => draftScheduledHandler(value, '$gte'),
  });

  const _getEndDatePickerProps = () => ({
    label: 'End Date',
    name: 'scheduled_date',
    style: styles.endDatePicker,
    value: draftFilter.scheduled_date?.$lte,
    disableBeforeDate: draftFilter.scheduled_date?.$gte,
    onChange: (value) => draftScheduledHandler(value, '$lte'),
  });

  const _getStatusFilterProps = (filterKey) => ({
    label: '',
    options: filterKey === 'status' ? statuses : channels,
    value: draftFilter[filterKey === 'status' ? 'status' : 'channel'],
    trackBy: 'value',
    labelKey: 'name',
    minWidth: 80,
    onChange: (val) => {
      setDraftFilter((f) => ({ ...f, [filterKey]: val.value }));
    },
  });

  const renderStartDatePicker = () => (
    <DatePicker {..._getStartDatePickerProps()} />
  );

  const renderEndDatePicker = () => (
    <DatePicker {..._getEndDatePickerProps()} />
  );

  const renderDatePickers = () => (
    <View style={styles.containerDatePicker}>
      {renderStartDatePicker()}
      {renderEndDatePicker()}
    </View>
  );

  const renderDateFilter = () => (
    <Layout.FilterItem>
      {renderDateLabel()}
      {renderDatePickers()}
    </Layout.FilterItem>
  );

  const renderStatusLabel = (label) => (
    <SociollaText style={styles.statusLabel}>{label}</SociollaText>
  );

  const renderStatusPicker = (value) => (
    <StatusPicker {..._getStatusFilterProps(value)} />
  );

  const renderStatusFilter = () => (
    <Layout.FilterItem>
      {renderStatusLabel('Status')}
      {renderStatusPicker('status')}
    </Layout.FilterItem>
  );

  const renderMarketTypeFilter = () => (
    <Layout.FilterItem lastItem>
      {renderStatusLabel('Channel')}
      {renderStatusPicker('channel')}
    </Layout.FilterItem>
  );

  const _getFilterProps = () => ({
    name: 'return-order',
    filterName: scope,
    isLocalVariable: true,
    hasFilterData: Object.keys(draftFilter).length > 0,
    onReset: () => {
      if (list.isFetching) {
        return;
      }
      setDraftFilter({});
      dispatch(paramsReseted({ key: scope }));
    },
    onApply: () => {
      if (list.isFetching) {
        return;
      }
      dispatch(filterAdded({ key: scope, filter: draftFilter }));
      dispatch(showPopup({ name: 'showFilter', value: false }));
    },
  });

  const renderLayoutFiler = () => (
    <Layout.Filter {..._getFilterProps()}>
      {renderDateFilter()}
      {renderStatusFilter()}
      {renderMarketTypeFilter()}
    </Layout.Filter>
  );

  const renderExportModal = () => (
    <ModalFull
      title="Export Return Orders"
      noTranslate={true}
      show={showReturnOrdersExport}
      onClose={() =>
        dispatch(
          showPopup({
            name: 'showReturnOrdersExport',
            value: false,
          }),
        )
      }>
      <View
        style={{
          height: isWeb
            ? 'calc(100vh - 150px)'
            : isMobileWeb
            ? 'calc(100dvh - 150px)'
            : dimension.height - (isAndroid ? 140 : 110),
        }}>
        <ScrollView
          contentContainerStyle={{
            width: '100%',
            ...(!isWeb ? { paddingBottom: 90 } : {}),
          }}>
          <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
            <Layout.FormGroup lastItem>
              <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                Date
              </SociollaText>
              <View
                style={{
                  width: '100%',
                  flexDirection: isWeb ? 'row' : 'column',
                  justifyContent: 'space-between',
                }}>
                <DatePicker
                  label="Start Date"
                  name="created_at"
                  style={{ width: isWeb ? '49%' : '100%' }}
                  value={filterExport.from_date}
                  onChange={(value) => {
                    setFilterExport({
                      ...filterExport,
                      from_date: formatISO(startOfDay(value)),
                    });
                  }}
                />
                <DatePicker
                  label="End Date"
                  name="created_at"
                  style={{ width: isWeb ? '49%' : '100%' }}
                  value={filterExport.to_date}
                  disableBeforeDate={
                    filterExport.from_date
                      ? filterExport.from_date
                      : addDays(new Date(), 1)
                  }
                  onChange={(value) => {
                    setFilterExport({
                      ...filterExport,
                      to_date: formatISO(endOfDay(value)),
                    });
                  }}
                />
              </View>
            </Layout.FormGroup>
            <Layout.FormGroup lastItem>
              <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                Channel
              </SociollaText>
              <StatusPicker
                label=""
                options={channelsExport}
                value={filterExport.channel}
                trackBy="value"
                labelKey="name"
                minWidth={80}
                onChange={(val) => {
                  setFilterExport({ ...filterExport, channel: val.value });
                }}
              />
            </Layout.FormGroup>
            <Layout.FormGroup lastItem>
              <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                Status
              </SociollaText>
              <StatusPicker
                label=""
                options={statuses}
                value={filterExport.status}
                trackBy="value"
                labelKey="name"
                minWidth={80}
                onChange={(val) => {
                  setFilterExport({ ...filterExport, status: val.value });
                }}
              />
            </Layout.FormGroup>
            <Layout.FormGroup lastItem>
              <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                Return Invoice status
              </SociollaText>
              <StatusPicker
                label=""
                options={return_invoice_statuses}
                value={filterExport.return_invoice_status}
                trackBy="value"
                labelKey="name"
                minWidth={80}
                onChange={(val) => {
                  setFilterExport({
                    ...filterExport,
                    return_invoice_status: val.value,
                  });
                }}
              />
            </Layout.FormGroup>
            <Layout.FormGroup lastItem>
              <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                Sales Team
              </SociollaText>
              <SalesTeamSearch
                value={filterExport.sales_team}
                placeholder="Select Sales Team"
                multiple={true}
                hideLabel
                customFilter={customSalesTeamFilter(userData)}
                onChange={(value) => {
                  setFilterExport({ ...filterExport, sales_team: value });
                }}
                allTeams
              />
            </Layout.FormGroup>
          </Layout.FormWrapper>
        </ScrollView>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: isWeb ? 'end' : 'space-between',
            paddingTop: isWeb ? 20 : 15,
            paddingBottom:
              (isWeb ? 20 : 15) + (isMobileWeb ? insets.bottom : 0),
            paddingHorizontal: isWeb ? 20 : 15,
            backgroundColor: Colors.white,
            ...(!isWeb
              ? {
                  position: isMobileWeb ? 'fixed' : 'absolute',
                  bottom: 0,
                  right: 0,
                  left: 0,
                }
              : {}),
          }}>
          <Button
            rounded={true}
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              backgroundColor: Colors.white,
              marginRight: 10,
            }}
            title={
              <SociollaText type="medium" style={{ fontSize: 14 }}>
                Cancel
              </SociollaText>
            }
            onPress={() =>
              dispatch(
                showPopup({
                  name: 'showReturnOrdersExport',
                  value: false,
                }),
              )
            }
          />
          <Button
            rounded={true}
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
            }}
            disabledStyle={{ width: 90 }}
            disabled={exporting}
            title={
              <SociollaText
                type="medium"
                style={{ fontSize: 14, color: Colors.white }}>
                {exporting ? 'Loading...' : 'Export'}
              </SociollaText>
            }
            onPress={() =>
              onExport().then(() => {
                dispatch(
                  showPopup({
                    name: 'showReturnOrdersExport',
                    value: false,
                  }),
                );
              })
            }
          />
        </View>
      </View>
    </ModalFull>
  );

  const renderAllContent = () => (
    <React.Fragment>
      {renderTitleWeb()}
      {renderTableContent()}
      {renderLayoutFiler()}
      {renderExportModal()}
    </React.Fragment>
  );

  // ── render ──
  return <Layout.Container>{renderAllContent()}</Layout.Container>;
};

const styles = StyleSheet.create({
  orderItem: {
    width: '100%',
    paddingVertical: 15,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  orderItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
  imageScheduleData: {
    width: 19,
    height: 14,
    marginLeft: -5,
    marginRight: 5,
    marginTop: -3,
  },
  totalAmountText: { fontSize: 12, color: '#293B77' },
  containerScheduleData: { flexDirection: 'row', alignItems: 'center' },
  containerReferenceCode: {
    marginTop: 12,
    marginBottom: 10,
    fontSize: 12,
    color: Colors.grey2,
  },
  scheduleText: { fontSize: 12, color: Colors.grey2 },
  customerNameText: { fontSize: 14, width: fullWidth - 140 },
  startDatePicker: {
    marginRight: isWeb ? 10 : 0,
    width: isWeb ? 'auto' : '50%',
  },
  endDatePicker: { width: isWeb ? 'auto' : '50%' },
  containerDatePicker: { width: '100%', flexDirection: 'row' },
  statusLabel: { fontSize: 13, marginBottom: 10 },
});

export default ReturnOrderScreen;
